import React, { RefObject, useCallback } from 'react'
import Icon from '../../icon'
import classnames from 'classnames/bind'
import styles from './styles.scss'
import type { ButtonWidth } from '../types'
import type { FilterProps } from '../index'
import { ColorNames } from '../../../../constants/colorVars'
const cx = classnames.bind(styles)

type FilterButtonProps = {
  filterButtonRef?: RefObject<HTMLButtonElement>
  icon?: string
  label: React.ReactNode
  width: ButtonWidth
  isFilterSelected: boolean
  onOpen: (isOpen: boolean) => void
  isOpen: boolean
  onReset: () => void
  elmId: string
  size?: FilterProps['size']
  variant?: 'default' | 'pill' | 'floating'
}

const FilterButton: React.FunctionComponent<FilterButtonProps> = ({
  filterButtonRef,
  icon,
  label,
  width,
  isFilterSelected,
  onOpen,
  isOpen,
  onReset,
  elmId,
  size = 'medium',
  variant = 'default',
}) => {
  const onClickRightButton = useCallback(() => {
    if (isFilterSelected && variant !== 'pill' && variant !== 'floating') {
      onReset()
      onOpen(false)
    } else {
      onOpen(!isOpen)
    }
  }, [isFilterSelected, isOpen, onReset, onOpen, variant])

  const sizeClass = {
    small: size === 'small',
    medium: size === 'medium',
  }

  return (
    <button
      type='button'
      ref={filterButtonRef}
      data-elm-id={`${elmId}`}
      aria-expanded={isOpen}
      aria-haspopup='dialog'
      className={cx(
        'filter-button',
        { active: isFilterSelected },
        { 'fluid-width': width === 'fluid' },
        { pill: variant === 'pill' },
        { floating: variant === 'floating' },
        sizeClass
      )}
    >
      <div className={cx('container')}>
        <div
          className={cx('label', sizeClass, {
            'full-radius': !!icon,
            'icon-only': !!icon,
          })}
          onClick={() => onOpen(!isOpen)}
        >
          <div data-elm-id={`${elmId}_left`} className={cx('label-container')}>
            {icon && (
              <Icon
                color={isFilterSelected ? ColorNames.Info : ColorNames.Gray}
                iconStyle='solid'
                name={icon}
                size='medium'
                fitted
              />
            )}
            <span
              className={cx('label-text', {
                'no-label': !!icon,
              })}
            >
              {label}
            </span>
          </div>
        </div>
        {!icon && variant !== 'floating' && (
          <div
            className={cx('right-button', sizeClass)}
            onClick={onClickRightButton}
            data-elm-id={`${elmId}_right`}
          >
            {isFilterSelected && variant !== 'pill' ? (
              <Icon
                color={ColorNames.Action}
                iconStyle='solid'
                name='xmark'
                size='medium'
                fitted
              />
            ) : (
              <Icon
                color={ColorNames.Gray}
                iconStyle='solid'
                name={isOpen ? 'chevron-up' : 'chevron-down'}
                size={variant === 'pill' ? 'small' : 'medium'}
                fitted
              />
            )}
          </div>
        )}
      </div>
    </button>
  )
}

export default FilterButton
